import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { toast } from '@/components/ui/use-toast';

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CreateProjectDialog = ({ open, onOpenChange }: CreateProjectDialogProps) => {
  const { addProject } = useSupabaseStore();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [aiContextMarkdown, setAiContextMarkdown] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast({ title: "Project name is required", variant: "destructive" });
      return;
    }
    setIsCreating(true);
    try {
      await addProject(name, description, aiContextMarkdown);
      toast({ title: "Project created successfully" });
      setName('');
      setDescription('');
      setAiContextMarkdown('');
      onOpenChange(false);
    } catch (error) {
      toast({ title: "Failed to create project", variant: "destructive" });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Project</DialogTitle>
          <DialogDescription>
            Projects help you group related chats together.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="name" className="text-right">
              Name
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="col-span-3"
              placeholder="e.g. Q3 Marketing Campaign"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="description" className="text-right">
              Description
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="col-span-3"
              placeholder="A brief description of the project."
            />
          </div>
          <div className="grid grid-cols-4 items-start gap-4">
            <Label htmlFor="ai-context-markdown" className="pt-2 text-right">
              Hidden AI context
            </Label>
            <div className="col-span-3 space-y-2">
              <Textarea
                id="ai-context-markdown"
                value={aiContextMarkdown}
                onChange={(e) => setAiContextMarkdown(e.target.value)}
                className="min-h-[180px] font-mono text-sm"
                placeholder={'# Private AI context\n- Audience: ...\n- Goals: ...\n- Constraints: ...'}
              />
              <p className="text-xs text-muted-foreground">
                Optional markdown instructions for the AI. This is stored with the project and stays hidden from chat participants.
              </p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={isCreating}>
            {isCreating ? 'Creating...' : 'Create Project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateProjectDialog;